<script>
  import { onMount } from "svelte";
  import { Modal } from "svelte-utilities";
  import { distSq } from "vector";
  import isEmpty from "lodash/isEmpty";

  import FlipIcon from "@local/assets/icons/flip-horizontal.svg";
  import SelectInput from "#lib/sidebar/SelectInput.svelte";
  import ItemsFromOpenings from "./ItemsFromOpenings.svelte";

  import {
    checkOpeningItemQuantities,
    getOpeningItemUpdates,
  } from "./scripts/check-opening-item-quantities.js";
  import { countHealableDrawingLinks, healDrawingLinks } from "./scripts/heal-item-drawings.js";
  import { locationsToRecreate, recreateLocations } from "./scripts/location-recreation.js";
  import locationsByDocument from "#src/extensions/locations-by-document.js";
  import {
    itemsWithOneMarker,
    openingsWithNoItems,
    repairMismatchedDrawings,
  } from "./scripts/items-with-one-marker.js";
  import { scaledItems } from "./scripts/scaled-items.js";
  import eb from "#src/extensions/event-bus.js";
  import { isLocationVisible } from "#src/extensions/filter-items.js";
  import { alerts } from "#src/stores/alerts.js";
  import {
    selectedLocations,
    selectedPages,
    hiddenLocations,
    documentViewShowItems,
    documentViewShowCollections,
    documentViewCollectionFilter,
    documentViewItemFilter,
    documentViewLocationFilter,
  } from "#src/stores/ui.js";
  import { locRecord } from "#src/extensions/filter-items.js";

  export let group;
  export let selectable = false;
  export let docid;
  export let path;
  export let items;
  export let collections;

  let modal;
  let currentUtility = null;
  let scaleItemsDirection = "horizontal";

  $: selectedLocationsList = Object.keys($selectedLocations)
    .map((id) => $group.locations[id])
    .filter((loc) => loc);
  $: sourceLocationFirst = true;
  $: sourceLocation = sourceLocationFirst ? selectedLocationsList[0] : selectedLocationsList[1];
  $: targetLocation = sourceLocationFirst ? selectedLocationsList[1] : selectedLocationsList[0];
  $: source = locRecord(sourceLocation, $group);
  $: target = locRecord(targetLocation, $group);

  function open() {
    modal.open();
  }

  function openingsQuantities() {
    checkOpeningItemQuantities($group);
  }

  function updateOpeningsQuantities() {
    const updates = getOpeningItemUpdates($group);
    if (updates.length) {
      group.update(updates);
      alerts.add({ type: "ok", message: `Updated ${updates.length} items to match opening quantity.` });
    } else {
      alerts.add({ type: "warning", message: "No items were updated." });
    }
  }

  function countDrawingLinks() {
    countHealableDrawingLinks($group);
  }

  function healDrawings() {
    const updates = healDrawingLinks($group);
    if (updates.length) {
      group.update(updates);
      alerts.add({ type: "ok", message: `Updated ${updates.length} item drawings.` });
    } else {
      alerts.add({ type: "warning", message: "No items were updated." });
    }
  }

  function countOpeningClips() {
    itemsWithOneMarker($group);
  }

  function repairOpeningClips() {
    const updates = repairMismatchedDrawings($group);
    if (updates.length) {
      group.update(updates);
      alerts.add({ type: "ok", message: `Updated ${updates.length} item drawings.` });
    } else {
      alerts.add({ type: "warning", message: "No items were updated." });
    }
  }

  function countNoItemOpenings() {
    openingsWithNoItems($group);
  }

  function previewMarkerCreation() {
    locationsToRecreate($group);
  }

  function logDeviceInfo() {
    console.log("Device Info:", {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      vendor: navigator.vendor,
      deviceMemory: navigator.deviceMemory,
      hardwareConcurrency: navigator.hardwareConcurrency,
      pixelRatio: window.devicePixelRatio,
      width: window.innerWidth,
      height: window.innerHeight,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      screenOrientation: window.screen.orientation?.type || null,
    });
  }

  function createScaledItems() {
    const updates = scaledItems($group, source, sourceLocation, target, targetLocation, scaleItemsDirection);
    if (updates.length) {
      group.update(updates);
      alerts.add({ type: "ok", message: `Created ${updates[0].records.length} scaled items.` });
    } else {
      alerts.add({ type: "warning", message: "No items were created." });
    }
    modal.close();
  }

  function associateWithNearestOpening() {
    try {
      if (isEmpty($selectedPages)) {
        throw new Error("No page selected. Please select pages to associate items with openings.");
      }

      const docid = path.split("/")[1];
      const origDoc = $group.documents[docid];

      if (!origDoc) {
        throw new Error("Document not found.");
      }

      const doc = origDoc.is_virtual ? $group.documents[origDoc.source_document_id] : origDoc;

      if (!doc) {
        throw new Error("Document not found.");
      }

      const filters = {
        show_items: $documentViewShowItems,
        show_collections: $documentViewShowCollections,
        location_filter: $documentViewLocationFilter,
        item_filter: $documentViewItemFilter,
        collection_filter: $documentViewCollectionFilter,
        hidden_locations: $hiddenLocations,
      };

      const docLocations = locationsByDocument($group)[doc.id] || [];
      const locations = docLocations.filter(
        (loc) => $selectedPages[loc.page_id] && isLocationVisible(loc, $group, filters),
      );
      const locsByPage = locations.reduce((acc, loc) => {
        if (!acc[loc.page_id]) {
          acc[loc.page_id] = [];
        }
        acc[loc.page_id].push(loc);
        return acc;
      }, {});

      let updates = [];
      Object.keys($selectedPages).forEach((pageId) => {
        const locs = locsByPage[pageId] || [];
        const collectionLocs = locs.filter(
          (loc) => loc.record_type === "collection" && $group.items[loc.record_id],
        );
        const itemLocs = locs.filter((loc) => loc.record_type === "item" && $group.items[loc.record_id]);
        if (!collectionLocs.length || !itemLocs.length) {
          return;
        }

        itemLocs.forEach((itemLoc) => {
          const d = collectionLocs.map((cLoc) => distSq(cLoc.position, itemLoc.position));
          const nearestIndex = d.indexOf(Math.min(...d));
          const nearestCollectionLoc = collectionLocs[nearestIndex];

          if (nearestCollectionLoc) {
            updates.push({
              type: "item",
              id: itemLoc.record_id,
              diff: {
                collection_id: nearestCollectionLoc.record_id,
              },
            });
          }
        });
      });

      if (!updates.length) {
        throw new Error("No items to update. Please ensure that selected pages contain items and openings.");
      }

      group.update(updates);
      alerts.add({ type: "ok", message: `Associated ${updates.length} items with nearest openings.` });
    } catch (error) {
      alerts.add({ type: "warning", message: error.message });
    }

    modal.close();
  }

  function recreateMarkers() {
    const updates = recreateLocations($group);
    if (updates.length) {
      const markers = updates[0].records.length;
      group.update(updates);
      alerts.add({ type: "ok", message: `Re-created ${markers} markers.` });
    } else {
      alerts.add({ type: "warning", message: "No markers were re-created." });
    }
  }

  onMount(() => {
    eb.on("open-utilities", open);

    return () => {
      eb.unsubscribe("open-utilities", open);
    };
  });
</script>

<Modal closeable bind:this={modal}>
  <div slot="title">Utilities</div>
  <div slot="content" class="text-xs space-y-4">
    {#if currentUtility === "scale-into-opening"}
      <button class="btn-text" on:click={() => (currentUtility = null)}>Back</button>
      <div>Copy scaled items from one opening to another</div>
      {#if selectedLocationsList.length === 2 && selectedLocationsList.every((loc) => loc.record_type === "collection")}
        <div class="flex items-center justify-between px-2">
          <div>
            <div>Source Opening:</div>
            <div>{source?.mark}</div>
          </div>
          <button on:click={() => (sourceLocationFirst = !sourceLocationFirst)}>
            <FlipIcon />
          </button>
          <div>
            <div>Target Opening:</div>
            <div>{target?.mark}</div>
          </div>
        </div>
        <SelectInput
          bind:value={scaleItemsDirection}
          label="Direction to scale items"
          options={[
            { label: "Horizontal", value: "horizontal" },
            { label: "Vertical", value: "vertical" },
            { label: "Both", value: "both" },
          ]} />
        <div class="flex justify-end">
          <button class="btn btn-primary" on:click={createScaledItems}>Create</button>
        </div>
      {:else}
        <div class="text-gray-500 italic">Select exactly two openings as a source and target opening.</div>
      {/if}
    {:else if currentUtility === "create-from-openings"}
      <button class="btn-text" on:click={() => (currentUtility = null)}>Back</button>
      <ItemsFromOpenings
        {group}
        {docid}
        {items}
        {collections}
        on:close={() => {
          currentUtility = null;
          modal.close();
        }} />
    {:else}
      <div class="space-y-1">
        <div>Item quantities:</div>
        <button on:click={openingsQuantities}>
          <div>Check quantities of items within openings</div>
        </button>
        <button on:click={updateOpeningsQuantities}>
          <div>Make quantities of items match openings</div>
        </button>
      </div>
      <div class="space-y-1">
        <div>Item Drawings and Markers:</div>
        <button on:click={countDrawingLinks}>
          <div>Count marker-drawing links which can be healed</div>
        </button>
        <button on:click={healDrawings}>
          <div>Heal link between item drawings and markers</div>
        </button>
        <button on:click={countOpeningClips}>
          <div>Count openings with exactly one clipped marker</div>
        </button>
        <button on:click={repairOpeningClips}>
          <div>Repair mismatched opening drawings with a single clip</div>
        </button>
        <button on:click={countNoItemOpenings}>
          <div>Count openings that contain no items</div>
        </button>
      </div>
      {#if selectable}
        <div class="space-y-1">
          <div>Items & Openings</div>
          <button on:click={() => (currentUtility = "create-from-openings")}>
            Create items from Openings
          </button>
          <button on:click={() => (currentUtility = "scale-into-opening")}>
            Copy scaled items from one opening to another
          </button>
          <button on:click={associateWithNearestOpening}> Associate items with nearest opening </button>
        </div>
      {/if}
      <div class="space-y-1">
        <div>Re-creation of markers from drawing:</div>
        <button on:click={previewMarkerCreation}>
          <div>Preview marker re-creation</div>
        </button>
        <button on:click={recreateMarkers}>
          <div>Re-create markers from item drawings</div>
        </button>
      </div>

      <div class="space-y-1">
        <div>Debug</div>
        <button on:click={logDeviceInfo}>
          <div>Log device information</div>
        </button>
      </div>
    {/if}
  </div>
</Modal>

<style lang="scss">
  button {
    @apply text-blue-500;

    &:hover {
      @apply underline;
    }
  }
</style>
